export interface AccountTypeGroupInfo {
  name: string;
  displayName: string;
  aliases?: string[];
  platform: string;
  initialCharge: number;
  spread: number;
  commission: number | 'Free' | 'Yes' | 'No';
  commissionFrom?: boolean;
  leverageMax: number;
  stopOut: number;
  minimumVolume: number;
  symbols: string;
  swap: 'Yes' | 'No';
  tradingConditions?: {
    volumeStep: number;
    islamic: boolean;
    baseCurrency: 'USD';
    pricingDecimalPlaces: number;
    maximumOpenOrders: 'Unlimited';
    maximumOrderVolume: number;
    maximumTotalVolume: number;
    executionType: 'MarketExecution';
  };
}

// Shared conditions for the six MQ account types specified in MET-967.
const mqTradingConditions = {
  baseCurrency: 'USD',
  pricingDecimalPlaces: 2,
  maximumOpenOrders: 'Unlimited',
  maximumOrderVolume: 10,
  maximumTotalVolume: 100,
  executionType: 'MarketExecution',
} as const;

export const accountTypeGroupData: AccountTypeGroupInfo[] = [
  {
    name: 'GOLD', displayName: 'GOLD', platform: 'MQ',
    initialCharge: 50, spread: 0, commission: 0.04, commissionFrom: true, leverageMax: 400,
    stopOut: 15, minimumVolume: 0.001, symbols: 'SymbolsGold', swap: 'Yes',
    tradingConditions: { ...mqTradingConditions, volumeStep: 0.001, islamic: true },
  },
  {
    name: 'NANO_B', displayName: 'NANO', aliases: ['NANO'], platform: 'MQ',
    initialCharge: 10, spread: 2, commission: 'No', leverageMax: 200,
    stopOut: 20, minimumVolume: 0.001, symbols: 'SymbolsNano', swap: 'Yes',
    tradingConditions: { ...mqTradingConditions, volumeStep: 0.001, islamic: true },
  },
  {
    name: 'STD2_B', displayName: 'STANDARD', aliases: ['STANDARD', 'STD2'], platform: 'MQ',
    initialCharge: 50, spread: 2, commission: 'No', leverageMax: 400,
    stopOut: 20, minimumVolume: 0.01, symbols: 'SymbolsStandardMq', swap: 'Yes',
    tradingConditions: { ...mqTradingConditions, volumeStep: 0.01, islamic: true },
  },
  {
    name: 'ECN_B', displayName: 'ECN', aliases: ['ECN'], platform: 'MQ',
    initialCharge: 100, spread: 0, commission: 0.04, commissionFrom: true, leverageMax: 500,
    stopOut: 30, minimumVolume: 0.01, symbols: 'SymbolsEcn', swap: 'Yes',
    tradingConditions: { ...mqTradingConditions, volumeStep: 0.01, islamic: true },
  },
  {
    name: 'Crypto', displayName: 'CRYPTO', platform: 'MQ',
    initialCharge: 100, spread: 1, commission: 1, commissionFrom: true, leverageMax: 50,
    stopOut: 40, minimumVolume: 0.001, symbols: 'Cryptocurrency', swap: 'Yes',
    tradingConditions: { ...mqTradingConditions, volumeStep: 0.001, islamic: false },
  },
  {
    name: 'STOCKS', displayName: 'STOCKS', platform: 'MQ',
    initialCharge: 300, spread: 2, commission: 1, commissionFrom: true, leverageMax: 10,
    stopOut: 40, minimumVolume: 1, symbols: 'SymbolsStocks', swap: 'Yes',
    tradingConditions: { ...mqTradingConditions, volumeStep: 1, islamic: false },
  },
  {
    name: 'ECN', displayName: 'ECN', platform: 'MT',
    initialCharge: 100, spread: 0, commission: 'Free', leverageMax: 100,
    stopOut: 30, minimumVolume: 0.01, symbols: 'SymbolsCurrencyPairsMetal', swap: 'Yes',
  },
  {
    name: 'nano', displayName: 'NANO', platform: 'MT',
    initialCharge: 10, spread: 1.5, commission: 'Free', leverageMax: 100,
    stopOut: 20, minimumVolume: 0.001, symbols: 'SymbolsCurrencyPairsMetals', swap: 'Yes',
  },
  {
    name: 'standard', displayName: 'STANDARD', platform: 'MT',
    initialCharge: 50, spread: 2, commission: 'Free', leverageMax: 100,
    stopOut: 20, minimumVolume: 0.01, symbols: 'SymbolsStandard', swap: 'Yes',
  },
  {
    name: 'ND', displayName: 'F50', aliases: ['NoDeposit', 'F50'], platform: 'MQ',
    initialCharge: 50, spread: 0, commission: 'Free', leverageMax: 100,
    stopOut: 30, minimumVolume: 0.01, symbols: 'SymbolsCurrencyPairsMetal', swap: 'No',
  },
];

export function findAccountTypeGroupInfo(
  platform: string,
  group: { code: string; name: string }
): AccountTypeGroupInfo | undefined {
  const candidates = accountTypeGroupData.filter(
    (account) => account.platform === platform?.toUpperCase()
  );
  // Codes identify the group; names also support the API's display-name variants.
  for (const value of [group.code, group.name]) {
    const match = candidates.find((account) =>
      [account.name, ...(account.aliases ?? [])].some(
        (alias) => alias.toLowerCase() === value.trim().toLowerCase()
      )
    );
    if (match) return match;
  }
  return undefined;
}
